/**
 * Catálogo y reglas de tipo de identificación (alineado a compra/SSPS).
 */

export interface TipoDocumento {
  abreviatura: string;
  descripcionTipoIdentificacion: string;
  tipoIdentificacionId?: number | string;
  descripcion?: string;
  nombre?: string;
  label?: string;
  [key: string]: unknown;
}

export interface TipoDocumentoOption {
  value: string;
  label: string;
  tipoIdentificacionId?: number;
  abreviatura: string;
}

/** Fallback idéntico al de compra/SSPS (+ ids para reset TV). */
export const TIPOS_DOCUMENTO_FALLBACK: TipoDocumento[] = [
  { abreviatura: 'NIT', descripcionTipoIdentificacion: 'NIT', tipoIdentificacionId: 1 },
  { abreviatura: 'CC', descripcionTipoIdentificacion: 'Cédula de Ciudadanía', tipoIdentificacionId: 2 },
  { abreviatura: 'CE', descripcionTipoIdentificacion: 'Cédula de Extranjería', tipoIdentificacionId: 3 },
  { abreviatura: 'PAS', descripcionTipoIdentificacion: 'Pasaporte', tipoIdentificacionId: 4 },
  { abreviatura: 'TI', descripcionTipoIdentificacion: 'Tarjeta de Identidad T.I.', tipoIdentificacionId: 5 },
  {
    abreviatura: 'PPT',
    descripcionTipoIdentificacion: 'Permiso de Permanencia Temporal P.P.T.',
    tipoIdentificacionId: 6
  }
];

const IDS_POR_ABREV: Record<string, number> = {
  NIT: 1,
  NI: 1,
  CC: 2,
  CE: 3,
  PAS: 4,
  PP: 4,
  TI: 5,
  PPT: 6,
  PT: 6
};

const SOLO_DIGITOS = new Set(['CC', 'CE', 'TI', 'NIT', 'NI']);
const ALFANUMERICOS = new Set(['PAS', 'PP', 'PEP', 'PPT', 'PT']);

export const MIN_NIT_DIGITOS = 7;
export const MAX_NIT_DIGITOS = 15;
/** Cuerpo NIT clásico (reset / postventa actuales). */
export const NIT_CUERPO_CLASICO = 9;

export function abreviaturaTipoDoc(t: unknown): string {
  if (typeof t === 'string' || typeof t === 'number') {
    return String(t ?? '')
      .toUpperCase()
      .trim();
  }
  const obj = t as TipoDocumento | null | undefined;
  return String(obj?.abreviatura ?? '')
    .toUpperCase()
    .trim();
}

/** Abreviatura canónica de UI (NI→NIT, PP→PAS, PT→PPT). */
export function abreviaturaCanon(abrev: unknown): string {
  const a = abreviaturaTipoDoc(abrev);
  if (a === 'NI' || a === 'NIT/RUT' || a.replace(/\s*\/\s*/g, '/') === 'NIT/RUT') {
    return 'NIT';
  }
  if (a === 'PP') {
    return 'PAS';
  }
  if (a === 'PT') {
    return 'PPT';
  }
  return a;
}

export function esAbreviaturaDocumentoNit(abrev: unknown): boolean {
  const a = abreviaturaCanon(abrev);
  return a === 'NIT';
}

export function filtrarTiposDocumentoPersonaNatural(
  tipos: TipoDocumento[] | null | undefined
): TipoDocumento[] {
  return (tipos || []).filter((t) => !esAbreviaturaDocumentoNit(t));
}

export function filtrarTiposDocumentoConNit(
  tipos: TipoDocumento[] | null | undefined
): TipoDocumento[] {
  return [...(tipos || [])];
}

export function etiquetaTipoDocumento(t: TipoDocumento | null | undefined): string {
  if (!t) {
    return '';
  }
  return String(
    t.descripcionTipoIdentificacion || t.descripcion || t.nombre || t.label || t.abreviatura || ''
  ).trim();
}

export function normalizarTiposDocumento(raw: unknown): TipoDocumento[] {
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { data?: unknown })?.data)
      ? ((raw as { data: unknown[] }).data as unknown[])
      : [];

  const mapped = arr
    .map((item) => {
      const t = item as TipoDocumento;
      const abrev = abreviaturaCanon(t);
      if (!abrev) {
        return null;
      }
      const idRaw = t.tipoIdentificacionId ?? IDS_POR_ABREV[abrev];
      const id = idRaw != null && idRaw !== '' ? Number(idRaw) : undefined;
      return {
        ...t,
        abreviatura: abrev,
        descripcionTipoIdentificacion: etiquetaTipoDocumento(t) || abrev,
        tipoIdentificacionId: Number.isFinite(id) ? id : IDS_POR_ABREV[abrev]
      } as TipoDocumento;
    })
    .filter((t): t is TipoDocumento => !!t);

  // Deduplicar por abreviatura canónica (preferir el primero con id).
  const byAbrev = new Map<string, TipoDocumento>();
  for (const t of mapped) {
    const key = abreviaturaCanon(t);
    if (!byAbrev.has(key)) {
      byAbrev.set(key, t);
    }
  }
  return Array.from(byAbrev.values());
}

export function aOpcionesSelect(tipos: TipoDocumento[]): TipoDocumentoOption[] {
  return tipos.map((t) => {
    const abrev = abreviaturaCanon(t);
    const id = Number(t.tipoIdentificacionId ?? IDS_POR_ABREV[abrev]);
    return {
      value: abrev,
      label: etiquetaTipoDocumento(t) || abrev,
      abreviatura: abrev,
      tipoIdentificacionId: Number.isFinite(id) ? id : undefined
    };
  });
}

/** Options con value = tipoIdentificacionId (reset TV). */
export function aOpcionesSelectPorId(tipos: TipoDocumento[]): TipoDocumentoOption[] {
  const out: TipoDocumentoOption[] = [];
  for (const t of tipos) {
    const abrev = abreviaturaCanon(t);
    const id = Number(t.tipoIdentificacionId ?? IDS_POR_ABREV[abrev]);
    if (!Number.isFinite(id) || id <= 0) {
      continue;
    }
    out.push({
      value: String(id),
      label: etiquetaTipoDocumento(t) || abrev,
      abreviatura: abrev,
      tipoIdentificacionId: id
    });
  }
  return out;
}

/**
 * Códigos legacy que aún esperan APIs de postventa (Canal histórico).
 * PAS→PP, PPT→PT, NIT se mantiene.
 */
export function abreviaturaParaApiPostventa(abrev: unknown): string {
  const a = abreviaturaCanon(abrev);
  if (a === 'PAS') {
    return 'PP';
  }
  if (a === 'PPT') {
    return 'PT';
  }
  return a;
}

export function tipoDocumentoRequiereSoloDigitos(abrev: unknown): boolean {
  return SOLO_DIGITOS.has(abreviaturaCanon(abrev));
}

export function tipoDocumentoPermiteAlfanumerico(abrev: unknown): boolean {
  return ALFANUMERICOS.has(abreviaturaCanon(abrev));
}

export function maxLengthNumeroDocumento(abrev: unknown): number {
  const a = abreviaturaCanon(abrev);
  if (a === 'NIT') {
    return NIT_CUERPO_CLASICO;
  }
  if (a === 'PAS') {
    return 9;
  }
  if (a === 'CC') {
    return 10;
  }
  return 12;
}

export function sanitizarNumeroDocumento(abrev: unknown, raw: unknown): string {
  const a = abreviaturaCanon(abrev);
  const max = maxLengthNumeroDocumento(a);
  let v = String(raw ?? '');
  if (tipoDocumentoRequiereSoloDigitos(a) || a === 'NIT') {
    v = v.replace(/\D/g, '');
  } else if (tipoDocumentoPermiteAlfanumerico(a)) {
    v = v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  } else {
    v = v.replace(/[.,\s]/g, '').toUpperCase();
  }
  v = v.replace(/^0+/, '');
  return v.slice(0, max);
}

/** true si el número NO es válido para el tipo. */
export function numeroDocumentoInvalido(abrev: unknown, numeroRaw: unknown): boolean {
  const a = abreviaturaCanon(abrev);
  const numero = String(numeroRaw ?? '').trim();
  if (!numero) {
    return true;
  }
  if (/^\d/.test(numero) && numero.startsWith('0')) {
    return true;
  }

  if (a === 'NIT') {
    return !/^\d+$/.test(numero) || numero.length !== NIT_CUERPO_CLASICO;
  }

  if (a === 'CC') {
    return !/^\d+$/.test(numero) || numero.length < 4 || numero.length > 10;
  }

  if (a === 'CE' || a === 'TI') {
    return !/^\d+$/.test(numero) || numero.length < 4 || numero.length > 12;
  }

  if (a === 'PAS') {
    return !/^[a-zA-Z0-9]{7,9}$/.test(numero);
  }

  if (a === 'PPT') {
    return numero.length < 4 || numero.length > 12 || !/^[a-zA-Z0-9]+$/.test(numero);
  }

  return numero.length === 0;
}

export function mensajeNumeroDocumentoInvalido(abrev: unknown): string {
  const a = abreviaturaCanon(abrev);
  if (a === 'NIT') {
    return 'El NIT debe tener exactamente 9 dígitos (sin el dígito de verificación).';
  }
  if (a === 'PAS') {
    return 'Pasaporte: letras y números, entre 7 y 9 caracteres.';
  }
  if (a === 'CC') {
    return 'Solo números, entre 4 y 10 dígitos.';
  }
  if (a === 'CE' || a === 'TI' || a === 'PPT') {
    return 'Identificación no válida (4 a 12 caracteres).';
  }
  return 'Número de identificación no válido.';
}

export function idTipoDocumentoPorAbreviatura(
  abrev: unknown,
  tipos: TipoDocumento[] | null | undefined
): number | null {
  const a = abreviaturaCanon(abrev);
  const found = (tipos || []).find((t) => abreviaturaCanon(t) === a);
  const id = Number(found?.tipoIdentificacionId ?? IDS_POR_ABREV[a]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function abreviaturaPorIdTipo(
  id: unknown,
  tipos: TipoDocumento[] | null | undefined
): string {
  const n = Number(id);
  const found = (tipos || []).find((t) => Number(t.tipoIdentificacionId) === n);
  if (found) {
    return abreviaturaCanon(found);
  }
  const entry = Object.entries(IDS_POR_ABREV).find(([, v]) => v === n);
  return entry ? abreviaturaCanon(entry[0]) : '';
}
